import React, { useState, useEffect, Suspense, lazy } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Services from './components/Services';
import AuthenticRituals from './components/AuthenticRituals';
import HairpieceSection from './components/HairpieceSection';
import HairpieceBillboard from './components/HairpieceBillboard';
import ProductsSection from './components/ProductsSection';
import BeforeAfterSlider from './components/BeforeAfterSlider';
import Gallery from './components/Gallery';
import Reviews from './components/Reviews';
import Contact from './components/Contact';
import Footer from './components/Footer';
import CookieConsentBanner from './components/CookieConsentBanner';
import SocialProofToast from './components/SocialProofToast';
import Toast from './components/Toast';
import InstallAppBanner from './components/InstallAppBanner';
import AppDownloadSection from './components/AppDownloadSection';
import SocialSidebar from './components/SocialSidebar';
import { salonInfo } from './data/salonInfo';
import { services } from './data/services';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import { playSuccessChime } from './utils/audioNotification';
import { initNativeApp, isNativePlatform, setupAndroidBackButton, setupDeepLinkListener } from './utils/capacitorNative';
import { Scissors, Sparkles, ShoppingBag, Film, MessageSquare, MapPin, ArrowRight } from 'lucide-react';

// 🚀 Code-splitting & Lazy loading heavy submodules
const BookingModal = lazy(() => import('./components/BookingModal'));
const CustomerAuthModal = lazy(() => import('./components/CustomerAuthModal'));
const CustomerProfileModal = lazy(() => import('./components/CustomerProfileModal'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const MobileBookingApp = lazy(() => import('./components/mobile/MobileBookingApp'));
const SpecialDaysPage = lazy(() => import('./components/SpecialDaysPage'));
const PrivacyPolicyModal = lazy(() => import('./components/PrivacyPolicyModal'));
const TermsModal = lazy(() => import('./components/TermsModal'));

function BarberApp() {
  const isNative = isNativePlatform();
  const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    searchParams?.get('source') === 'pwa'
  );
  const isAppMode = isNative || isStandalone || searchParams?.get('app') === '1' || (typeof window !== 'undefined' && window.location.hash.includes('app=1'));

  useEffect(() => {
    initNativeApp();
  }, []);

  if (isAppMode) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-slate-900 flex items-center justify-center text-amber-400 font-bold">Laden...</div>}>
        <MobileBookingApp />
      </Suspense>
    );
  }

  const { lang, t } = useLanguage();
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isCookieSettingsOpen, setIsCookieSettingsOpen] = useState(false);
  const [isSpecialDaysOpen, setIsSpecialDaysOpen] = useState(false);
  const [selectedServiceForBooking, setSelectedServiceForBooking] = useState(null);
  const [selectedDateForBooking, setSelectedDateForBooking] = useState(null);
  const [selectedGenderForBooking, setSelectedGenderForBooking] = useState(null);
  const [selectedCatForBooking, setSelectedCatForBooking] = useState(null);
  const [toast, setToast] = useState(null);

  // 🧭 Multi-Page State & Hash Routing
  const getPageFromHash = () => {
    if (typeof window === 'undefined') return 'home';
    const hash = window.location.hash.toLowerCase();
    if (hash.startsWith('#hizmetler') || hash.startsWith('#services')) return 'services';
    if (hash.startsWith('#haarwerk') || hash.startsWith('#hairpiece')) return 'hairpiece';
    if (hash.startsWith('#producten') || hash.startsWith('#products') || hash.startsWith('#urunler')) return 'products';
    if (hash.startsWith('#galeri') || hash.startsWith('#gallery')) return 'gallery';
    if (hash.startsWith('#yorumlar') || hash.startsWith('#reviews')) return 'reviews';
    if (hash.startsWith('#iletisim') || hash.startsWith('#contact')) return 'contact';
    return 'home';
  };

  const [currentPage, setCurrentPage] = useState(getPageFromHash);

  const handleNavigate = (pageId) => {
    setCurrentPage(pageId);
    const hashMapping = {
      home: '#home',
      services: '#hizmetler',
      hairpiece: '#haarwerk',
      products: '#producten',
      gallery: '#galeri',
      reviews: '#yorumlar',
      contact: '#iletisim'
    };
    const targetHash = hashMapping[pageId] || '#home';
    if (window.location.hash !== targetHash) {
      window.history.pushState(null, '', targetHash);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 📱 Initialize Android Native LifeCycle, Back Button & Deep Links
  useEffect(() => {
    initNativeApp();

    const cleanupBackButton = setupAndroidBackButton(() => {
      if (isSpecialDaysOpen) { setIsSpecialDaysOpen(false); return true; }
      if (isBookingModalOpen) { setIsBookingModalOpen(false); return true; }
      if (isAdminOpen) { setIsAdminOpen(false); return true; }
      if (isAuthModalOpen) { setIsAuthModalOpen(false); return true; }
      if (isProfileModalOpen) { setIsProfileModalOpen(false); return true; }
      if (isPrivacyOpen) { setIsPrivacyOpen(false); return true; }
      if (isTermsOpen) { setIsTermsOpen(false); return true; }
      if (isCookieSettingsOpen) { setIsCookieSettingsOpen(false); return true; }
      if (currentPage !== 'home') { handleNavigate('home'); return true; }
      return false;
    });

    const cleanupDeepLink = setupDeepLinkListener((url) => {
      if (url.includes('payment-success')) {
        const hashPart = url.includes('#') ? url.split('#')[1] : '';
        if (hashPart) window.location.hash = hashPart;
      }
    });

    return () => {
      cleanupBackButton();
      cleanupDeepLink();
    };
  }, [
    isBookingModalOpen, isAdminOpen, isAuthModalOpen, isProfileModalOpen,
    isPrivacyOpen, isTermsOpen, isCookieSettingsOpen, isSpecialDaysOpen, currentPage
  ]);

  // Direct URL Hash / Query Admin Access (#admin or ?admin) & Payment Return Callbacks
  useEffect(() => {
    const handleHashCheck = () => {
      const hash = window.location.hash.toLowerCase();
      const rawHash = window.location.hash;
      const search = window.location.search.toLowerCase();
      if (hash === '#admin' || hash === '#yonetici' || search.includes('admin=true') || search.includes('admin=1')) {
        setIsAdminOpen(true);
      }
      if (hash === '#ozel-gunler' || hash === '#feestdagen' || hash === '#special-days' || hash === '#acties') {
        setIsSpecialDaysOpen(true);
      }
      if (hash.startsWith('#randevu') || hash.startsWith('#afspraak') || hash.startsWith('#booking')) {
        setIsBookingModalOpen(true);
      }

      // Multi-Page Router sync
      const resolvedPage = getPageFromHash();
      if (resolvedPage) {
        setCurrentPage(resolvedPage);
      }
      if (hash.startsWith('#payment-success')) {
        playSuccessChime();
        try {
          import('canvas-confetti').then(({ default: conf }) => {
            conf({
              particleCount: 150,
              spread: 90,
              origin: { y: 0.5 },
              colors: ['#10b981', '#f59e0b', '#3b82f6', '#d97706']
            });
          }).catch(() => {});
        } catch (err) {}

        // Extract appointment ID & Amount
        const queryString = rawHash.includes('?') ? rawHash.split('?')[1] : window.location.search.replace(/^\?/, '');
        const params = new URLSearchParams(queryString);
        const aptId = params.get('id');
        const paidAmountVal = parseFloat(params.get('amount')) || 0;

        if (aptId) {
          try {
            const raw = localStorage.getItem('aurahair_appointments');
            const allApps = raw ? JSON.parse(raw) : [];
            const target = allApps.find(a => a.id === aptId);
            const isFullPaid = target ? (paidAmountVal >= target.totalPrice || target.totalPrice <= 50 || !target.isDepositRequired) : true;

            const updatedApps = allApps.map(item => {
              if (item.id === aptId) {
                return {
                  ...item,
                  isPaid: isFullPaid ? true : item.isPaid,
                  isDepositPaid: true,
                  depositStatus: 'paid',
                  paymentStatus: 'paid',
                  paidAmount: paidAmountVal || item.totalPrice,
                  paymentMethod: 'mollie_ideal',
                  paidAt: new Date().toISOString(),
                  status: 'confirmed'
                };
              }
              return item;
            });
            localStorage.setItem('aurahair_appointments', JSON.stringify(updatedApps));
            window.dispatchEvent(new Event('storage'));
          } catch (e) {}

          fetch(`/api/appointments/${encodeURIComponent(aptId)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              isPaid: true,
              isDepositPaid: true,
              depositStatus: 'paid',
              paymentStatus: 'paid',
              paidAmount: paidAmountVal,
              paymentMethod: 'mollie_ideal',
              paidAt: new Date().toISOString(),
              status: 'confirmed'
            })
          }).catch(() => {});
        }

        showToast({
          type: 'success',
          title: lang === 'nl' ? '✅ Betaling Geslaagd!' : lang === 'tr' ? '✅ iDEAL Ödemeniz Başarılı!' : '✅ Payment Successful!',
          message: lang === 'nl' ? 'Uw betaling is ontvangen via Mollie iDEAL.' : lang === 'tr' ? 'Mollie iDEAL ödemeniz başarıyla alındı ve randevunuz onaylandı.' : 'Your payment has been confirmed via Mollie.'
        });
        window.history.replaceState(null, '', window.location.pathname);
      }
    };
    handleHashCheck();
    window.addEventListener('hashchange', handleHashCheck);
    return () => window.removeEventListener('hashchange', handleHashCheck);
  }, [lang]);

  const showToast = ({ type = 'info', title, message }) => {
    setToast({ type, title, message });
    setTimeout(() => {
      setToast(null);
    }, 4500);
  };

  const handleOpenAdmin = () => {
    window.location.hash = 'admin';
    setIsAdminOpen(true);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const handleCloseAdmin = () => {
    setIsAdminOpen(false);
    if (window.location.hash === '#admin' || window.location.hash === '#yonetici') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  };

  const handleOpenBooking = (service = null, date = null, gender = null, catFilter = null) => {
    setSelectedServiceForBooking(service);
    setSelectedDateForBooking(date);
    setSelectedGenderForBooking(gender || (service ? service.gender : null));
    setSelectedCatForBooking(catFilter || (service ? service.category : null));
    setIsBookingModalOpen(true);
  };

  const handleSelectCampaignForBooking = (campaign) => {
    setIsSpecialDaysOpen(false);
    if (campaign && campaign.start_date) {
      handleOpenBooking(null, campaign.start_date);
    } else {
      handleOpenBooking(null, null);
    }
  };

  const handleBookIntake = () => {
    const intakeService = services.find(s => s.id === 'hairpiece-intake') || services[0];
    handleOpenBooking(intakeService);
  };

  const handleBookingSuccess = (appointment) => {
    showToast({
      type: 'success',
      title: lang === 'nl' ? 'Afspraak Bevestigd!' : lang === 'tr' ? 'Randevunuz Kaydedildi!' : 'Appointment Confirmed!',
      message: `${appointment.id} - ${salonInfo.name} Rotterdam`
    });
  };

  const handleAuthSuccess = (user) => {
    showToast({
      type: 'success',
      title: lang === 'nl' ? `Welkom, ${user.name}!` : lang === 'tr' ? `Hoş Geldiniz, ${user.name}!` : `Welcome, ${user.name}!`,
      message: lang === 'nl' ? 'U bent succesvol ingelogd.' : lang === 'tr' ? 'Hesabınıza başarıyla giriş yapıldı.' : 'Logged in successfully.'
    });
  };

  // If Admin Panel is open, it renders full-screen standalone page / POS system
  if (isAdminOpen) {
    return (
      <Suspense fallback={<div className="fixed inset-0 bg-slate-950 flex items-center justify-center text-amber-400 font-bold">Admin Panel Laden...</div>}>
        <div className="fixed inset-0 w-screen h-screen z-[99999] overflow-hidden bg-slate-950 text-white font-sans selection:bg-amber-500 selection:text-white">
          <AdminPanel
            isOpen={true}
            onClose={handleCloseAdmin}
            onShowToast={showToast}
            onOpenBookingModal={() => {
              handleCloseAdmin();
              handleOpenBooking(null);
            }}
          />
          {toast && (
            <Toast
              type={toast.type}
              title={toast.title}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          )}
        </div>
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-stone-50 text-stone-800 font-sans selection:bg-amber-500 selection:text-white">
      
      {/* 🌐 Top-Left Floating Blue Social Media Dock */}
      <SocialSidebar />

      {/* Top Fixed Navbar */}
      <Navbar
        currentPage={currentPage}
        onNavigate={handleNavigate}
        onOpenBooking={() => handleOpenBooking(null)}
        onOpenAdmin={handleOpenAdmin}
        onOpenAuth={() => setIsAuthModalOpen(true)}
        onOpenProfile={() => setIsProfileModalOpen(true)}
        onOpenSpecialDays={() => setIsSpecialDaysOpen(true)}
      />

      {/* Main Content Multi-Page Sections */}
      <main className="w-full max-w-full overflow-x-hidden pt-24 sm:pt-28">
        
        {/* ================= PAGE 1: HOME ================= */}
        {currentPage === 'home' && (
          <div className="space-y-0 animate-fade-in">
            {/* 👑 1. TOP: 3-Split Full-Width Billboard (3'e Bölünmüş Popüler Hizmet Vitrini) */}
            <HairpieceBillboard 
              onSelectService={(service) => handleOpenBooking(service)}
              onOpenBooking={() => handleOpenBooking(null)}
              onOpenGenderBooking={(gender, catId) => handleOpenBooking(null, null, gender, catId)}
              onNavigate={handleNavigate}
            />

            {/* 👑 2. HERO: "Tarzını & Saçını Keşfet" with Randevu buttons & Photo Slider */}
            <Hero 
              onOpenBooking={(srv) => handleOpenBooking(srv)}
              onBookIntake={handleBookIntake}
              onOpenAuth={() => setIsAuthModalOpen(true)}
              onOpenProfile={() => setIsProfileModalOpen(true)}
              onNavigate={handleNavigate}
            />

            {/* 👑 3. Quick Explore / Department Navigation Cards */}
            <section className="py-12 bg-white border-y border-slate-200">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-8">
                  <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Aura Hair Rotterdam</span>
                  <h3 className="text-2xl sm:text-3xl font-black text-slate-900 mt-1">
                    {lang === 'nl' ? 'Ontdek Onze Salon & Diensten' : lang === 'tr' ? 'Bölümlere Göz Atın' : 'Explore Our Salon'}
                  </h3>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
                  {[
                    { id: 'services', label: t.nav.services, desc: lang === 'nl' ? 'Dames & Heren Knippen, Kleuren' : lang === 'tr' ? 'Kadın & Erkek Kesim, Boya' : 'Women & Men Cuts, Colors', icon: Scissors, color: 'from-amber-500 to-amber-600' },
                    { id: 'hairpiece', label: t.nav.hairpiece, desc: lang === 'nl' ? 'Niet-Chirurgisch Haarwerk' : lang === 'tr' ? 'Ameliyatsız Saç Sistemleri' : 'Non-Surgical Hair Systems', icon: Sparkles, color: 'from-emerald-600 to-teal-700' },
                    { id: 'products', label: t.nav.products || (lang === 'nl' ? 'Producten' : lang === 'tr' ? 'Ürünler' : 'Products'), desc: lang === 'nl' ? 'Professionele Verzorging' : lang === 'tr' ? 'Profesyonel Saç Bakım' : 'Professional Hair Care', icon: ShoppingBag, color: 'from-blue-600 to-indigo-700' },
                    { id: 'gallery', label: t.nav.gallery, desc: lang === 'nl' ? 'Foto & 4K Video Galerij' : lang === 'tr' ? 'Foto & 4K Video Galerisi' : 'Photo & 4K Video Gallery', icon: Film, color: 'from-purple-600 to-pink-600' },
                    { id: 'reviews', label: t.nav.reviews, desc: lang === 'nl' ? '5.0 Google Reviews' : lang === 'tr' ? '5.0 Google Değerlendirmeleri' : '5.0 Google Reviews', icon: MessageSquare, color: 'from-amber-600 to-yellow-600' },
                    { id: 'contact', label: t.nav.contact, desc: lang === 'nl' ? 'Adres & Routebeschrijving' : lang === 'tr' ? 'Adres & Yol Tarifi' : 'Address & Directions', icon: MapPin, color: 'from-slate-800 to-slate-900' }
                  ].map(card => {
                    const Icon = card.icon;
                    return (
                      <button
                        key={card.id}
                        onClick={() => handleNavigate(card.id)}
                        className="p-4 rounded-2xl bg-slate-50 hover:bg-amber-50/50 border border-slate-200 hover:border-amber-400 text-left transition-all duration-300 hover:scale-105 hover:shadow-lg group cursor-pointer flex flex-col justify-between space-y-3"
                      >
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${card.color} text-white flex items-center justify-center shadow-md group-hover:rotate-6 transition-transform`}>
                          <Icon className="w-5 h-5 stroke-[2.5]" />
                        </div>
                        <div>
                          <h4 className="text-xs sm:text-sm font-black text-slate-900 group-hover:text-amber-700 transition-colors">
                            {card.label}
                          </h4>
                          <p className="text-[10px] text-slate-600 mt-0.5 line-clamp-1">
                            {card.desc}
                          </p>
                        </div>
                        <div className="text-[10px] font-bold text-amber-700 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                          <span>{lang === 'nl' ? 'Bekijk Pagina' : lang === 'tr' ? 'Sayfayı Aç' : 'View Page'}</span>
                          <ArrowRight className="w-3 h-3" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Authentic Rituals */}
            <AuthenticRituals 
              onOpenBooking={() => handleOpenBooking(null)}
            />

            {/* 📱 Smart App Download Section (Strict iOS / Android Separation) */}
            <AppDownloadSection />
          </div>
        )}

        {/* ================= PAGE 2: SERVICES & PRICES ================= */}
        {currentPage === 'services' && (
          <div className="space-y-0 animate-fade-in">
            {/* Subpage Breadcrumb Bar */}
            <div className="bg-slate-900 border-b border-slate-800 pl-14 pr-3 sm:px-8 py-2.5 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleNavigate('home')} 
                  className="text-amber-400 hover:text-amber-300 font-black flex items-center gap-1 cursor-pointer transition-colors"
                >
                  <span>← {lang === 'nl' ? 'Home' : lang === 'tr' ? 'Ana Sayfa' : 'Home'}</span>
                </button>
                <span className="text-slate-600">/</span>
                <span className="text-slate-200 font-bold">{lang === 'nl' ? 'Diensten & Tarieven' : lang === 'tr' ? 'Hizmetler & Fiyatlar' : 'Services & Prices'}</span>
              </div>
              <button 
                onClick={() => handleOpenBooking(null)} 
                className="px-3 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow cursor-pointer transition-all hover:scale-105 active:scale-95"
              >
                {t.nav.bookNow}
              </button>
            </div>

            {/* Page Banner */}
            <div className="pt-10 pb-8 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-white text-center border-b border-slate-800 px-4">
              <span className="px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 text-xs font-bold uppercase tracking-wider inline-block mb-3">
                Aura Hair Rotterdam
              </span>
              <h1 className="text-3xl sm:text-5xl font-black font-serif tracking-tight">
                {lang === 'nl' ? 'Diensten & Tarieven' : lang === 'tr' ? 'Hizmetler & Fiyat Listesi' : 'Services & Pricing'}
              </h1>
              <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
                {lang === 'nl' 
                  ? 'Exclusieve dames- en herenbehandelingen, haarkleuring, balayage, keratine en specialistische haarverzorging.'
                  : lang === 'tr'
                    ? 'Kadın ve erkek saç kesimi, renklendirme, keratin botoks ve saç şekillendirme uygulamaları.'
                    : 'Exclusive hair styling, coloring, highlights, keratin treatments and premium haircare.'}
              </p>
            </div>

            <Services 
              onSelectService={(service) => handleOpenBooking(service)}
              onOpenBooking={() => handleOpenBooking(null)}
            />

            <div id="transformatie">
              <BeforeAfterSlider 
                onBookIntake={handleBookIntake}
              />
            </div>
          </div>
        )}

        {/* ================= PAGE 3: HAIRPIECE & PROTEZ SAÇ ================= */}
        {currentPage === 'hairpiece' && (
          <div className="space-y-0 animate-fade-in">
            {/* Subpage Breadcrumb Bar */}
            <div className="bg-slate-900 border-b border-slate-800 pl-14 pr-3 sm:px-8 py-2.5 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleNavigate('home')} 
                  className="text-amber-400 hover:text-amber-300 font-black flex items-center gap-1 cursor-pointer transition-colors"
                >
                  <span>← {lang === 'nl' ? 'Home' : lang === 'tr' ? 'Ana Sayfa' : 'Home'}</span>
                </button>
                <span className="text-slate-600">/</span>
                <span className="text-emerald-300 font-bold">{lang === 'nl' ? 'Haarwerk & Protez Saç' : lang === 'tr' ? 'Protez Saç Sistemleri' : 'Hair Systems'}</span>
              </div>
              <button 
                onClick={handleBookIntake} 
                className="px-3 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow cursor-pointer transition-all hover:scale-105 active:scale-95"
              >
                {lang === 'nl' ? 'Gratis Intake' : lang === 'tr' ? 'Ücretsiz Ön Görüşme' : 'Free Consultation'}
              </button>
            </div>

            {/* Page Banner */}
            <div className="pt-10 pb-8 bg-gradient-to-b from-slate-900 via-emerald-950/60 to-slate-950 text-white text-center border-b border-slate-800 px-4">
              <span className="px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-xs font-bold uppercase tracking-wider inline-block mb-3">
                100% Human Hair &bull; Niet-Chirurgisch
              </span>
              <h1 className="text-3xl sm:text-5xl font-black font-serif tracking-tight">
                {lang === 'nl' ? 'Niet-Chirurgisch Haarwerk' : lang === 'tr' ? 'Ameliyatsız Protez Saç Sistemleri' : 'Non-Surgical Hair Systems'}
              </h1>
              <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
                {lang === 'nl'
                  ? 'Krijg uw zelfvertrouwen terug met onze natuurlijke, ademende en onzichtbare haarsystemen. Gratis intake consultatie.'
                  : lang === 'tr'
                    ? 'Doğal saç çizgisine sahip, nefes alan tül tabanlı ameliyatsız protez saç ile gençleşin. Ücretsiz ön görüşme randevusu alın.'
                    : 'Regain your confidence with undetectable, premium real human hair systems. Free intake consultation.'}
              </p>
            </div>

            <HairpieceSection 
              onBookIntake={handleBookIntake}
            />

            <div id="transformatie">
              <BeforeAfterSlider 
                onBookIntake={handleBookIntake}
              />
            </div>
          </div>
        )}

        {/* ================= PAGE 4: PRODUCTS / BOUTIQUE ================= */}
        {currentPage === 'products' && (
          <div className="space-y-0 animate-fade-in">
            {/* Subpage Breadcrumb Bar */}
            <div className="bg-slate-900 border-b border-slate-800 pl-14 pr-3 sm:px-8 py-2.5 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleNavigate('home')} 
                  className="text-amber-400 hover:text-amber-300 font-black flex items-center gap-1 cursor-pointer transition-colors"
                >
                  <span>← {lang === 'nl' ? 'Home' : lang === 'tr' ? 'Ana Sayfa' : 'Home'}</span>
                </button>
                <span className="text-slate-600">/</span>
                <span className="text-blue-300 font-bold">{lang === 'nl' ? 'Producten' : lang === 'tr' ? 'Ürünler & Butik' : 'Products'}</span>
              </div>
              <button 
                onClick={() => handleOpenBooking(null)} 
                className="px-3 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow cursor-pointer transition-all hover:scale-105 active:scale-95"
              >
                {t.nav.bookNow}
              </button>
            </div>

            {/* Page Banner */}
            <div className="pt-10 pb-8 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-white text-center border-b border-slate-800 px-4">
              <span className="px-3 py-1 rounded-full bg-blue-500/20 border border-blue-400/40 text-blue-300 text-xs font-bold uppercase tracking-wider inline-block mb-3">
                Salon Boutique
              </span>
              <h1 className="text-3xl sm:text-5xl font-black font-serif tracking-tight">
                {lang === 'nl' ? 'Exclusieve Verzorgingsproducten' : lang === 'tr' ? 'Salon Butik & Saç Bakım Ürünleri' : 'Exclusive Care Products'}
              </h1>
              <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
                {lang === 'nl'
                  ? 'Professionele shampoos, conditioners, haarserums en stylingproducten rechtstreeks uit onze salon.'
                  : lang === 'tr'
                    ? 'Salonda kullandığımız profesyonel bakım serileri, sülfatsız şampuanlar ve styling waxları.'
                    : 'Professional salon hair care lines, serums, organic shampoos and styling products.'}
              </p>
            </div>

            <ProductsSection 
              onOpenBooking={() => handleOpenBooking(null)}
            />
          </div>
        )}

        {/* ================= PAGE 5: GALLERY & VIDEOS ================= */}
        {currentPage === 'gallery' && (
          <div className="space-y-0 animate-fade-in">
            {/* Subpage Breadcrumb Bar */}
            <div className="bg-slate-900 border-b border-slate-800 pl-14 pr-3 sm:px-8 py-2.5 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleNavigate('home')} 
                  className="text-amber-400 hover:text-amber-300 font-black flex items-center gap-1 cursor-pointer transition-colors"
                >
                  <span>← {lang === 'nl' ? 'Home' : lang === 'tr' ? 'Ana Sayfa' : 'Home'}</span>
                </button>
                <span className="text-slate-600">/</span>
                <span className="text-purple-300 font-bold">{lang === 'nl' ? 'Gallerij' : lang === 'tr' ? 'Foto & Video Galeri' : 'Gallery'}</span>
              </div>
              <button 
                onClick={() => handleOpenBooking(null)} 
                className="px-3 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow cursor-pointer transition-all hover:scale-105 active:scale-95"
              >
                {t.nav.bookNow}
              </button>
            </div>

            <Gallery onOpenBooking={() => handleOpenBooking(null)} />
          </div>
        )}

        {/* ================= PAGE 6: REVIEWS ================= */}
        {currentPage === 'reviews' && (
          <div className="space-y-0 animate-fade-in">
            {/* Subpage Breadcrumb Bar */}
            <div className="bg-slate-900 border-b border-slate-800 pl-14 pr-3 sm:px-8 py-2.5 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleNavigate('home')} 
                  className="text-amber-400 hover:text-amber-300 font-black flex items-center gap-1 cursor-pointer transition-colors"
                >
                  <span>← {lang === 'nl' ? 'Home' : lang === 'tr' ? 'Ana Sayfa' : 'Home'}</span>
                </button>
                <span className="text-slate-600">/</span>
                <span className="text-yellow-300 font-bold">{lang === 'nl' ? 'Reviews' : lang === 'tr' ? 'Müşteri Yorumları' : 'Reviews'}</span>
              </div>
              <button 
                onClick={() => handleOpenBooking(null)} 
                className="px-3 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow cursor-pointer transition-all hover:scale-105 active:scale-95"
              >
                {t.nav.bookNow}
              </button>
            </div>

            {/* Page Banner */}
            <div className="pt-10 pb-8 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-white text-center border-b border-slate-800 px-4">
              <span className="px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 text-xs font-bold uppercase tracking-wider inline-block mb-3">
                5.0 ★ ★ ★ ★ ★ &bull; Google Reviews
              </span>
              <h1 className="text-3xl sm:text-5xl font-black font-serif tracking-tight">
                {lang === 'nl' ? 'Klantenbeoordelingen' : lang === 'tr' ? 'Müşteri Yorumları & Deneyimler' : 'Customer Reviews'}
              </h1>
              <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
                {lang === 'nl'
                  ? 'Lees wat onze gewaardeerde klanten zeggen over hun ervaring bij Aura Hair Rotterdam.'
                  : lang === 'tr'
                    ? "Rotterdam'daki mutlu müşterilerimizin salonumuz hakkındaki gerçek değerlendirmeleri."
                    : 'Real reviews and ratings from our valued clients at Aura Hair Rotterdam.'}
              </p>
            </div>

            <Reviews />
          </div>
        )}

        {/* ================= PAGE 7: CONTACT & ROUTE ================= */}
        {currentPage === 'contact' && (
          <div className="space-y-0 animate-fade-in">
            {/* Subpage Breadcrumb Bar */}
            <div className="bg-slate-900 border-b border-slate-800 pl-14 pr-3 sm:px-8 py-2.5 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleNavigate('home')} 
                  className="text-amber-400 hover:text-amber-300 font-black flex items-center gap-1 cursor-pointer transition-colors"
                >
                  <span>← {lang === 'nl' ? 'Home' : lang === 'tr' ? 'Ana Sayfa' : 'Home'}</span>
                </button>
                <span className="text-slate-600">/</span>
                <span className="text-emerald-300 font-bold">{lang === 'nl' ? 'Contact' : lang === 'tr' ? 'İletişim & Konum' : 'Contact'}</span>
              </div>
              <button 
                onClick={() => handleOpenBooking(null)} 
                className="px-3 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow cursor-pointer transition-all hover:scale-105 active:scale-95"
              >
                {t.nav.bookNow}
              </button>
            </div>

            {/* Page Banner */}
            <div className="pt-10 pb-8 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-white text-center border-b border-slate-800 px-4">
              <span className="px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-xs font-bold uppercase tracking-wider inline-block mb-3">
                Samuel Esmeijerplein 3, Rotterdam
              </span>
              <h1 className="text-3xl sm:text-5xl font-black font-serif tracking-tight">
                {lang === 'nl' ? 'Contact & Locatie' : lang === 'tr' ? 'İletişim, Konum & Ulaşım' : 'Contact & Location'}
              </h1>
              <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
                {lang === 'nl'
                  ? 'Gratis parkeergelegenheid direct voor de deur. Op slechts 3 minuten loopafstand van metrostation Oosterflank.'
                  : lang === 'tr'
                    ? 'Kapı önünde ücretsiz otopark ve Oosterflank metro istasyonuna yalnızca 3 dakika yürüme mesafesinde.'
                    : 'Free parking directly in front of the salon. Only 3 minutes walk from Oosterflank metro.'}
              </p>
            </div>

            <Contact />
          </div>
        )}

      </main>

      {/* Footer with Legal Info & Modals */}
      <Footer 
        onNavigate={handleNavigate}
        onOpenBooking={() => handleOpenBooking(null)}
        onOpenAdmin={handleOpenAdmin}
        onOpenPrivacy={() => setIsPrivacyOpen(true)}
        onOpenTerms={() => setIsTermsOpen(true)}
        onOpenCookies={() => setIsCookieSettingsOpen(true)}
        onOpenSpecialDays={() => setIsSpecialDaysOpen(true)}
      />

      {/* ================= MODALS & POPUPS ================= */}

      {/* 🎁 2026 Feestdagen & Speciale Kortingskalender Pagina */}
      <Suspense fallback={null}>
        {isSpecialDaysOpen && (
          <SpecialDaysPage
            isOpen={isSpecialDaysOpen}
            onClose={() => {
              setIsSpecialDaysOpen(false);
              if (window.location.hash === '#ozel-gunler' || window.location.hash === '#feestdagen' || window.location.hash === '#special-days' || window.location.hash === '#acties') {
                window.history.replaceState(null, '', window.location.pathname + window.location.search);
              }
            }}
            onSelectCampaignForBooking={handleSelectCampaignForBooking}
          />
        )}
      </Suspense>

      {/* Online Booking Modal Popup */}
      {isBookingModalOpen && (
        <Suspense fallback={null}>
          <BookingModal
            isOpen={isBookingModalOpen}
            onClose={() => {
              setIsBookingModalOpen(false);
              setSelectedServiceForBooking(null);
              setSelectedDateForBooking(null);
              setSelectedGenderForBooking(null);
              setSelectedCatForBooking(null);
            }}
            initialService={selectedServiceForBooking}
            initialDate={selectedDateForBooking}
            initialGender={selectedGenderForBooking}
            initialCatFilter={selectedCatForBooking}
            onBookingSuccess={handleBookingSuccess}
            onOpenAuth={() => setIsAuthModalOpen(true)}
            onOpenSpecialDaysCalendar={() => {
              setIsBookingModalOpen(false);
              setIsSpecialDaysOpen(true);
            }}
          />
        </Suspense>
      )}

      {/* Customer Login / Register Modal */}
      {isAuthModalOpen && (
        <Suspense fallback={null}>
          <CustomerAuthModal
            isOpen={isAuthModalOpen}
            onClose={() => setIsAuthModalOpen(false)}
            onAuthSuccess={handleAuthSuccess}
          />
        </Suspense>
      )}

      {/* Customer Profile & Loyalty Card Modal */}
      {isProfileModalOpen && (
        <Suspense fallback={null}>
          <CustomerProfileModal
            isOpen={isProfileModalOpen}
            onClose={() => setIsProfileModalOpen(false)}
            onOpenBooking={() => {
              setIsProfileModalOpen(false);
              handleOpenBooking(null);
            }}
          />
        </Suspense>
      )}

      <Suspense fallback={null}>
        {/* 🔒 Official Dutch Privacyverklaring Modal (AVG / GDPR) */}
        {isPrivacyOpen && (
          <PrivacyPolicyModal
            isOpen={isPrivacyOpen}
            onClose={() => setIsPrivacyOpen(false)}
          />
        )}

        {/* 📄 Official Dutch Algemene Voorwaarden Modal */}
        {isTermsOpen && (
          <TermsModal
            isOpen={isTermsOpen}
            onClose={() => setIsTermsOpen(false)}
          />
        )}
      </Suspense>

      {/* 🍪 AVG & ePrivacy Compliant Cookie Consent Banner & Preferences Modal */}
      <CookieConsentBanner
        onOpenPrivacy={() => setIsPrivacyOpen(true)}
        isOpenCustomModal={isCookieSettingsOpen}
        onCloseCustomModal={() => setIsCookieSettingsOpen(false)}
      />

      {/* ⚡ FLOATING QUICK APPOINTMENT BUTTON (Accessible from every page) */}
      <aside aria-label="Quick Booking" className="fixed bottom-24 sm:bottom-6 right-4 sm:right-5 z-30">
        <button
          type="button"
          onClick={() => handleOpenBooking(null)}
          className="btn-gold-sheen group relative flex items-center gap-2.5 px-4 sm:px-5 py-3 sm:py-3.5 rounded-full bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 text-white font-black text-xs sm:text-sm uppercase tracking-wider shadow-[0_10px_25px_-5px_rgba(245,158,11,0.5)] hover:shadow-[0_15px_35px_-5px_rgba(245,158,11,0.7)] hover:scale-105 active:scale-95 transition-all border border-amber-300/60 cursor-pointer"
          title={lang === 'nl' ? 'Direct Afspraak Maken' : lang === 'tr' ? 'Hemen Kolay Randevu Al' : 'Book Appointment Online'}
        >
          <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-emerald-500 border-2 border-white"></span>
          </span>
          <Scissors className="w-4 h-4 text-white group-hover:rotate-12 transition-transform" />
          <span className="font-extrabold font-sans">
            {lang === 'nl' ? '⚡ Afspraak Maken' : lang === 'tr' ? '⚡ Kolay Randevu Al' : '⚡ Book Now'}
          </span>
        </button>
      </aside>

      {/* 📱 Android & Mobile App Direct Install Floating Banner */}
      <InstallAppBanner lang={lang} />

      {/* Global Toast Notification */}
      {toast && (
        <Toast
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

    </div>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BarberApp />
      </AuthProvider>
    </LanguageProvider>
  );
}
